import React from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {
  Filter,
  Button,
  SortingRule,
  Table,
  TableContainer,
} from '@odin-labs/components';
import { ApolloError } from '@apollo/client';
import {
  GetJobsiteFormSubmissionsOrderByField,
  useGenerateFormSubmissionsReportMutation,
  useGetJobsiteFormSubmissionsQuery,
  WorkerResponseFilter,
} from 'apollo/generated/client-operations';
import type { MergeUnionType } from 'types';
import { LoadingError } from 'components/loadingError';
import { AuthContext } from 'auth';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import {
  evalJsCode,
  isStringEnumValue,
  momentISODateFormatter,
  useQueryOrderBy,
  useResettableState,
} from 'utils';
import { resolveFilterDate } from 'utils/dateRangeFilters';
import { getGraphQLError } from 'utils/error';
import { paginationSizePerPage as limit } from 'utils/constants';
import { getJobsiteIdsForDevelopers } from 'utils/filters';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { SortOrder } from 'utils/useQueryOrderBy';
import { ArrowToBottomIcon } from 'components/icons';
import { AlertService } from 'components/alertService';
import { useDependencies } from 'containers/jobsiteFormSubmission/utils';
import { RemoveFormSubmissionModal } from 'containers/forms/modals/RemoveFormSubmissionModal';
import { CustomFormColumnsOptions } from 'containers/jobsiteFormSubmission/types';
import {
  FormSubmissionColumn,
  FormSubmissionsWidgetProps,
  FormSubmission,
  FormSubmissionFilters,
} from './types';
import {
  getColumns,
  getFilterItems,
  orderByFields,
} from './FormSubmissionsWidget.tables';

export function FormSubmissionsWidget(
  props: FormSubmissionsWidgetProps,
): React.ReactElement {
  const {
    onTabApiChange,
    form,
    workerId,
    extraColumns: propsExtraColumns,
    filters: filterKeys,
    navigateInNewTab: openInNewTab,
    dateRange: persistedDateRange,
    setDateRange,
  } = props as MergeUnionType<FormSubmissionsWidgetProps>;

  const { currentUser: user } = React.useContext(AuthContext);
  const history = useHistory();

  const {
    value: formSubmissionToDelete,
    setValue: openRemoveFormSubmissionModal,
    resetValue: closeRemoveFormSubmissionModal,
  } = useResettableState<FormSubmission>(null);

  // Seed defaults from the shared range when provided (e.g. the worker profile
  // tabs), falling back to this widget's own defaults otherwise.
  const defaultStartDate = resolveFilterDate(
    persistedDateRange?.startDate,
    moment().startOf('day').subtract(1, 'month'),
  );
  const defaultEndDate = resolveFilterDate(
    persistedDateRange?.endDate,
    moment().startOf('day'),
  );

  const {
    page,
    orderBy: defaultSortField,
    orderByDesc: isDescending,
    startDate,
    endDate,
    developerIds,
    jobsiteIds,
    contractorIds,
    workerIds,
    responses,
    trainings,
    formIds,
    createdByIds,
    hasAttachments,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    updateJobsiteSelection: true,
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
  });
  const offset = page * limit;

  const { tableSortInfo, orderBy, setNewOrderBy } = useQueryOrderBy<
    string,
    GetJobsiteFormSubmissionsOrderByField
  >(
    defaultSortField,
    isDescending,
    (tableField: string): GetJobsiteFormSubmissionsOrderByField =>
      orderByFields[tableField],
  );

  const tableSortBy: Array<SortingRule<string>> = tableSortInfo?.dataField
    ? [{ id: tableSortInfo.dataField, desc: tableSortInfo.order === 'desc' }]
    : [];

  const {
    data: formSubmissionData,
    loading: formSubmissionsLoading,
    error: formSubmissionsError,
    refetch,
  } = useGetJobsiteFormSubmissionsQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    variables: {
      jobsiteFormSubmissionsInput: {
        paginationInput: { limit, offset },
        startDate: startDate.startOf('day').toDate(),
        endDate: endDate.endOf('day').toDate(),
        formIds: (form?.id && [form.id]) ?? formIds,
        workerIds: workerId ? [workerId] : workerIds,
        jobsiteIds,
        contractorIds,
        createdByIds,
        hasAttachments,
        responses: responses?.filter((response) =>
          isStringEnumValue(WorkerResponseFilter, response),
        ),
        trainings,
        orderBy,
      },
    },
    skip: isUrlLoading || (!form && !workerId),
  });

  const {
    jobsites,
    loading: jobsitesLoading,
    error: jobsitesError,
  } = useAvailableJobsites();

  const {
    edges,
    count: totalCount,
    filtersOptions,
  } = formSubmissionData?.getCurrentSession.user.jobsiteFormSubmissions ?? {};
  const jobsiteFormSubmissions = edges?.map(({ node }) => node);
  const pageCount = Math.ceil(totalCount / limit);

  React.useEffect(() => onTabApiChange({ refetchData: refetch }), [refetch]);

  // Keep the shared container range (when provided) in sync with the resolved
  // date filter so sibling tabs inherit it. Keyed on the formatted values so it
  // fires on real changes only; only the date keys are written, preserving any
  // time persisted by another tab on merge.
  const syncedStartDate = startDate?.format(momentISODateFormatter);
  const syncedEndDate = endDate?.format(momentISODateFormatter);
  React.useEffect(() => {
    if (isUrlLoading) return;
    setDateRange?.({ startDate: syncedStartDate, endDate: syncedEndDate });
  }, [setDateRange, syncedStartDate, syncedEndDate, isUrlLoading]);

  const navigateToFormSubmissionEditPage = React.useCallback(
    (formSubmission: FormSubmission): void => {
      const path = `/forms/${formSubmission.jobsiteForm.form.key}/${formSubmission.id}`;
      if (openInNewTab) {
        window.open(path, '_blank');
      } else {
        history.push(path);
      }
    },
    [history, openInNewTab],
  );

  const navigateToFormSubmissionPrintPage = React.useCallback(
    (formSubmission: FormSubmission): void => {
      const path = `/forms/${formSubmission.jobsiteForm.form.key}/${formSubmission.id}/print`;
      if (openInNewTab) {
        window.open(path, '_blank');
      } else {
        history.push(path);
      }
    },
    [history, openInNewTab],
  );

  const {
    columns: columnsExpression,
    columnsOptions: columnsOptionsExpression,
    filters: filtersExpression,
  } = form?.content ?? {};
  const { dependencies: dependenciesExpressions } = form?.content.edit ?? {};
  const dependencies = useDependencies(dependenciesExpressions);
  const filterHeaders = form?.content.filterHeaders;

  const columns = React.useMemo(() => {
    const columnsOptions =
      (dependencies &&
        columnsOptionsExpression &&
        evalJsCode<CustomFormColumnsOptions>(columnsOptionsExpression, {
          ctx: { dependencies },
        })) ??
      {};
    const extraColumns =
      propsExtraColumns ??
      (dependencies &&
        columnsExpression &&
        evalJsCode<FormSubmissionColumn[]>(columnsExpression, {
          ctx: { dependencies },
        })) ??
      [];
    return getColumns({
      displayedByForm: !!form,
      extraColumns,
      columnsOptions,
      user,
      navigateToFormSubmissionPrintPage,
      navigateToFormSubmissionEditPage,
      openRemoveFormSubmissionModal,
    });
  }, [
    form,
    dependencies,
    columnsOptionsExpression,
    columnsExpression,
    propsExtraColumns,
    user,
    navigateToFormSubmissionPrintPage,
    navigateToFormSubmissionEditPage,
    openRemoveFormSubmissionModal,
  ]);

  const dynFilterKeys = React.useMemo(() => {
    return (
      dependencies &&
      filtersExpression &&
      evalJsCode<Array<keyof FormSubmissionFilters>>(filtersExpression, {
        ctx: { dependencies },
      })
    );
  }, [dependencies, filtersExpression]);

  const filterItems = React.useMemo(() => {
    return getFilterItems({
      // converts null to undefined, so that default filter keys can be applied
      filterKeys: dynFilterKeys ?? filterKeys ?? undefined,
      startDate,
      endDate,
      jobsites,
      filtersOptions,
      developerIds,
      jobsiteIds,
      contractorIds,
      workerIds,
      responses,
      trainings,
      filterHeaders,
      formIds,
      createdByIds,
      hasAttachments,
    });
  }, [
    filterKeys,
    dynFilterKeys,
    startDate,
    endDate,
    jobsites,
    filtersOptions,
    developerIds,
    jobsiteIds,
    contractorIds,
    workerIds,
    responses,
    trainings,
    filterHeaders,
    formIds,
    createdByIds,
    hasAttachments,
  ]);

  const [
    generateFormSubmissionsReport,
    { loading: generateFormSubmissionsLoading },
  ] = useGenerateFormSubmissionsReportMutation({
    onError: (apolloError: ApolloError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(apolloError),
      );
    },
    onCompleted: (response) => {
      window.open(response?.generateFormSubmissionsReport?.downloadUrl);
    },
  });

  const loading: boolean = formSubmissionsLoading || jobsitesLoading;
  const error: ApolloError = formSubmissionsError || jobsitesError;

  if (error) {
    return <LoadingError error={error} />;
  }

  const onSortByChangeHandler = (
    sortBy: Array<SortingRule<FormSubmission>>,
  ): void => {
    const [firstSortBy] = sortBy ?? [];
    const sortField = firstSortBy?.id ?? null;
    const sortOrder: SortOrder = firstSortBy?.desc ? 'desc' : 'asc';
    const newOrderBy = sortField && { field: sortField, order: sortOrder };
    setNewOrderBy(newOrderBy);
    updateUrl({
      orderBy: sortField,
      orderByDesc: sortField && sortOrder === 'desc' ? true : null,
    });
  };

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({
      page: pageIndex ? pageIndex + 1 : null,
    });
  };

  const onFilterChangeHandler = (
    changedFilters: Partial<FormSubmissionFilters>,
  ): void => {
    const { dateRange: dateRangeFilter, ...restChangedFilters } =
      changedFilters;
    const dateRange =
      dateRangeFilter === null
        ? { startDate: null, endDate: null }
        : dateRangeFilter;
    updateUrl({
      page: null,
      ...dateRange,
      ...restChangedFilters,
      ...getJobsiteIdsForDevelopers(jobsites, changedFilters.developerIds),
    });
  };

  const onRowClickHandler = ({
    data: jobsiteFormSubmission,
  }: {
    data: FormSubmission;
  }): void => {
    if (jobsiteFormSubmission.jobsiteForm.form.allowPrintView) {
      navigateToFormSubmissionPrintPage(jobsiteFormSubmission);
    }
  };

  return (
    <TableContainer>
      <Filter
        items={filterItems}
        loading={loading}
        onChange={onFilterChangeHandler}
      >
        {!!form && (
          <Button
            theme="white"
            size={['base', 'md:xs']}
            text="Download"
            hideTextOnMobile
            icon={ArrowToBottomIcon}
            onClick={(): void => {
              generateFormSubmissionsReport({
                variables: {
                  input: {
                    formId: form.id,
                    startDate: startDate.startOf('day').toDate(),
                    endDate: endDate.endOf('day').toDate(),
                    // the same filters as the table
                    workerIds: workerId ? [workerId] : workerIds,
                    jobsiteIds,
                    contractorIds,
                    createdByIds,
                    hasAttachments,
                    responses: responses?.filter((response) =>
                      isStringEnumValue(WorkerResponseFilter, response),
                    ),
                    trainings,
                    orderBy,
                  },
                },
              });
            }}
            withSpinner={generateFormSubmissionsLoading}
          />
        )}
      </Filter>
      <Table
        loading={loading}
        columns={columns}
        data={jobsiteFormSubmissions}
        initialState={{ sortBy: tableSortBy, pageSize: limit }}
        pageCount={pageCount}
        pageIndex={page}
        remote
        enablePagination
        onRowClick={onRowClickHandler}
        onPageChange={onPageChangeHandler}
        onSortByChange={onSortByChangeHandler}
      />
      <RemoveFormSubmissionModal
        isOpen={!!formSubmissionToDelete}
        onCancel={closeRemoveFormSubmissionModal}
        onConfirm={closeRemoveFormSubmissionModal}
        form={form}
        formSubmission={formSubmissionToDelete}
      />
    </TableContainer>
  );
}
